import React, { FC, useMemo } from 'react'
import { graphql, useFragment } from 'react-relay'
import { useScript } from 'hoofd'
import urlParser from 'js-video-url-parser'

import { HOST_NAME } from '@data/config/url'
import { OG_IMAGE_WIDTH, OG_IMAGE_HEIGHT } from '@data/config/socialCard'

import { Metadata } from '../Metadata'
import { buildBreadcrumbList, HOME_BREADCRUMB } from '../buildBreadcrumbList'

import { PresentationMetadata_presentation$key } from './__generated__/PresentationMetadata_presentation.graphql'

export const PresentationMetadata: FC<{
  presentation: PresentationMetadata_presentation$key
  title?: string
  description?: string
}> = ({
  presentation,
  title: overridenTitle,
  description: overridenDescription,
}) => {
  const {
    createdAt,
    description,
    presenter,
    presenterFirstName,
    presenterLastName,
    title: presentationTitle,
    videoSourceUrl,
    prettyUrl,
    updatedAt,
  } = useFragment(
    graphql`
      fragment PresentationMetadata_presentation on Presentation {
        title
        description

        presenterFirstName
        presenterLastName

        videoSourceUrl
        prettyUrl

        createdAt
        updatedAt

        presenter {
          firstName
          lastName
          slugId
          primaryPhoto {
            seoUrl: transformUrl(
              transform: { w: 500, h: 500, dpr: 1, fit: "contain" }
            )
          }
        }
      }
    `,
    presentation
  )

  const photo = useMemo(() => {
    if (videoSourceUrl) {
      const videoInfo = urlParser.parse(videoSourceUrl)

      if (videoInfo?.provider === 'youtube') {
        const thumbnailUrl = urlParser.create({
          format: 'longImage',
          params: { imageQuality: 'maxresdefault' },
          videoInfo,
        })

        if (thumbnailUrl) {
          // YouTube maxresdefault thumbnails are 1280x720
          return { url: thumbnailUrl, width: 1280, height: 720 }
        }
      }
    }

    return {
      url: `${HOST_NAME}/social/presentation/${prettyUrl}/card.png?${updatedAt}`,
      width: OG_IMAGE_WIDTH,
      height: OG_IMAGE_HEIGHT,
    }
  }, [videoSourceUrl, prettyUrl, updatedAt])

  const name = presenter
    ? `${presenter.firstName} ${presenter.lastName}`
    : `${presenterFirstName} ${presenterLastName}`

  const title = overridenTitle || `${presentationTitle} by ${name}`

  const canonicalUrl = `${HOST_NAME}/presentations/${prettyUrl}`

  const contentDescription =
    overridenDescription || description || `${presentationTitle} by ${name}`

  const structuredData = useMemo(() => {
    const videoName = presentationTitle || `Presentation by ${name}`
    const videoDescription = contentDescription || videoName

    return JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'CreativeWork',
      name: presentationTitle,
      url: canonicalUrl,
      description: contentDescription,
      author: {
        '@type': 'Person',
        name,
        ...(presenter?.slugId
          ? { url: `${HOST_NAME}/${presenter.slugId}` }
          : {}),
        ...(presenter?.primaryPhoto?.seoUrl
          ? { image: presenter.primaryPhoto.seoUrl }
          : {}),
      },
      datePublished: createdAt,
      dateModified: updatedAt,
      ...(photo.url ? { thumbnailUrl: photo.url } : {}),
      ...(videoSourceUrl
        ? {
            video: {
              '@type': 'VideoObject',
              contentUrl: videoSourceUrl,
              name: videoName,
              description: videoDescription,
              thumbnailUrl: photo.url,
              uploadDate: createdAt || updatedAt,
            },
          }
        : {}),
    })
  }, [
    presentationTitle,
    canonicalUrl,
    contentDescription,
    name,
    presenter,
    photo,
    videoSourceUrl,
    createdAt,
    updatedAt,
    prettyUrl,
  ])

  useScript({
    type: 'application/ld+json',
    text: structuredData,
  })

  const breadcrumbData = useMemo(
    () =>
      buildBreadcrumbList([
        HOME_BREADCRUMB,
        { name: 'Presentations', url: `${HOST_NAME}/presentations` },
        { name: presentationTitle, url: canonicalUrl },
      ]),
    [presentationTitle, canonicalUrl]
  )

  useScript({ type: 'application/ld+json', text: breadcrumbData })

  return (
    <Metadata
      canonicalUrl={canonicalUrl}
      description={contentDescription}
      imageTransformParams='NONE'
      imageWidth={photo.width}
      imageHeight={photo.height}
      ogType='article'
      photoUrl={photo.url}
      title={title}
    />
  )
}
